import {
  AccountsUser,
  AclPermissions,
  AclPermissionsRole,
  Permissions,
} from '@eventmanager/types';
import { DEFAULT_ACL_ROLE, ACTIVE_ROLES } from '../config';
import { UserAuthState } from '../enums';
import { RootState } from '../store';

export const emptyPermissions: AclPermissions = {
  role: DEFAULT_ACL_ROLE,
  advancedLinkBuilder: false,
  advancedSettings: false,
  attendeeTracking: false,
  broadcastRecordings: false,
  customMaps: false,
  dataAnalytics: false,
  isActive: true,
  aiGeneratedEvents: 0,
  maxEventDuration: 0.5, // hours
  maxFloors: 2,
  maxAttendees: 150,
  monthlyFee: 0,
  // [2026-04-01] Options that are or are not available depending on the user's current subscription plan.
  landingPageCustomization: false,
  mobileEventAccess: false,
  readySecurity: false,
  dedicatedSupportAgent: false,
  whiteLabeling: false,
  // [2026-03-07] Replaces subscriptionId for a more accurate reference.
  stripePriceId: '', // Stripe recurring Price ID associated with the plan.
  maxConcurrentEvents: 0,
  is24x7: false,
};

export function currentUser(state: RootState): AccountsUser | null {
  return state.account.user;
}

export function currentUserAuthState(state: RootState): UserAuthState {
  return state.account.authState;
}

export function currentUserPermissions(state: RootState): {
  permissions: AclPermissions;
  overridden: string[];
} {
  const user = currentUser(state);
  const role = user?.role || DEFAULT_ACL_ROLE;
  const planPermissions: AclPermissions =
    getPermission(role)(state) || emptyPermissions;
  const permissionOverrides = user?.permissionOverrides || {};
  return Object.keys(permissionOverrides).reduce(
    (memo, keyName) => {
      if (permissionOverrides[keyName as keyof Permissions]) {
        return {
          permissions: {
            ...memo.permissions,
            [keyName]: permissionOverrides[keyName as keyof Permissions],
          },
          overridden: [...memo.overridden, keyName],
        };
      }
      return memo;
    },
    {
      permissions: planPermissions,
      overridden: [] as string[],
    },
  );
}

// [2026-03-07] Added the pro role to active plans to support plan upgrades.
// Roles starter, pro, and enterprise should appear as active subscription plans,
// while the "free" role is excluded.
export function availableUpgradePlans(state: RootState): AclPermissions[] {
  return ACTIVE_ROLES.map(
    planName => state.account.permissions[planName as AclPermissionsRole],
  ).filter(v => !!v && v.role !== DEFAULT_ACL_ROLE);
}

export function permissionsMap(state: RootState) {
  return state.account.permissions;
}

export function getPermission(
  role: AclPermissionsRole,
): (state: RootState) => AclPermissions | null {
  return (state: RootState) => state.account?.permissions[role] || null;
}

export const getPermissionValue =
  (permissionName: keyof AclPermissions) => (state: RootState) => {
    const { permissions } = currentUserPermissions(state);
    return permissions[permissionName];
  };

export function maxEventDurationHours(state: RootState): number {
  return currentUserPermissions(state)?.permissions.maxEventDuration || 0;
}

export function maxFloorsPerEvent(state: RootState): number {
  return currentUserPermissions(state)?.permissions.maxFloors || 0;
}
